import React from 'react';

const BookingPrintA4 = ({ booking, customer }) => {
  if (!booking) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return num.toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return 'Not Set';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return `${value.length} items`;
    if (typeof value === 'object') {
      if (value.mode && value.amount) {
        return `${value.mode}: ₹${value.amount}`;
      }
      return JSON.stringify(value);
    }
    return String(value);
  };

  const formatFieldName = (field) => {
    const fieldMap = {
      'booking_type': 'Booking Type',
      'weight': 'Weight',
      'public_remark': 'Customer Note',
      'internal_remark': 'Internal Note',
      'booking_date': 'Booking Date',
      'customer_id': 'Customer',
      'advance_payments': 'Payments'
    };

    return fieldMap[field] || field
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Calculate totals
  const calculateTotals = () => {
    if (!booking || !booking.items) {
      return { subtotal: 0, totalAdvance: 0, metalCredit: 0, balance: 0, totalWeight: 0 };
    }

    const totalWeight = booking.items.reduce((sum, item) => sum + (parseFloat(item.weight) || 0), 0);
    
    let subtotal = 0;
    if (booking.base24k_rate_ex_gst && totalWeight > 0) {
      subtotal = (totalWeight * booking.base24k_rate_ex_gst) / 10;
    }

    const advancePaid = (booking.advance_payments || []).reduce((sum, p) => {
      if (p.mode === 'Metal Credit') return sum;
      const amount = parseFloat(p.amount) || 0;
      return p.transaction_type === 'REFUND' ? sum - amount : sum + amount;
    }, 0);

    const metalAdvance = (booking.advance_payments || []).reduce((sum, p) => {
      if (p.mode === 'Metal Credit' && p.advance_equivalent) {
        return sum + parseFloat(p.advance_equivalent);
      }
      return sum;
    }, 0);

    const metalCredit = (booking.advance_payments || []).reduce((sum, p) => {
      if (p.mode === 'Metal Credit') {
        // Check if payment has deposits array with details
        if (p.deposits && Array.isArray(p.deposits) && p.deposits.length > 0) {
          return sum + p.deposits.reduce((depSum, dep) => depSum + parseFloat(dep.fine_wt || 0), 0);
        }
        // Fallback to old structure
        if (p.fine_wt) {
          return sum + parseFloat(p.fine_wt);
        }
      }
      // Old Gold should NOT be included in Metal Credit total
      return sum;
    }, 0);

    const totalAdvance = advancePaid + metalAdvance;
    const balance = subtotal - totalAdvance;

    return { subtotal, totalAdvance, metalCredit, balance, totalWeight };
  };

  const totals = calculateTotals();

  // Group payments by mode
  const groupPaymentsByMode = () => {
    const grouped = {};
    (booking.advance_payments || []).forEach(payment => {
      const mode = payment.mode || 'Unknown';
      if (!grouped[mode]) {
        grouped[mode] = [];
      }
      grouped[mode].push(payment);
    });
    return grouped;
  };

  const paymentsByMode = groupPaymentsByMode();

  const renderRevisionChange = (change, idx) => {
    if (change.type === 'modified') {
      return (
        <div key={idx} style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '3px' }}>{formatFieldName(change.field)}</div>
          <div style={{ fontSize: '9px' }}>
            <span style={{ color: '#dc2626' }}>Old: {formatValue(change.old)}</span>
            <span style={{ margin: '0 5px' }}>→</span>
            <span style={{ color: '#059669' }}>New: {formatValue(change.new)}</span>
          </div>
        </div>
      );
    }

    if (change.type === 'item_modified') {
      const getItemDisplayName = () => {
        const itemType = change.booking_type || 'Item';
        const weight = change.weight;
        let displayName = itemType;
        if (weight) displayName += ` (${weight}g)`;
        return displayName;
      };
      
      return (
        <div key={idx} style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '4px', color: '#7c3aed' }}>
            📦 Item Modified: {getItemDisplayName()}
          </div>
          <div style={{ marginLeft: '10px' }}>
            {change.changes.map((itemChange, subIdx) => (
              <div key={subIdx} style={{ fontSize: '9px', marginBottom: '3px' }}>
                <div style={{ fontWeight: 'bold' }}>{formatFieldName(itemChange.field)}</div>
                <div style={{ color: '#dc2626' }}>- {formatValue(itemChange.old)}</div>
                <div style={{ color: '#059669' }}>+ {formatValue(itemChange.new)}</div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (change.type === 'payment_added') {
      const isRefund = change.new?.transaction_type === 'REFUND';
      return (
        <div key={idx} style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '3px', color: isRefund ? '#dc2626' : '#059669' }}>
            {isRefund ? '↩️ Refund Added' : '💰 Payment Added'}
          </div>
          <div style={{ fontSize: '9px' }}>
            <span style={{ fontWeight: 'bold' }}>{change.new?.mode}:</span>{' '}
            <span style={{ fontWeight: 'bold', color: isRefund ? '#dc2626' : '#059669' }}>
              {isRefund ? '-' : ''}₹{change.new?.amount?.toLocaleString('en-IN')}
            </span>
            {change.new?.date && (
              <span> on {new Date(change.new.date).toLocaleDateString('en-IN')}</span>
            )}
          </div>
        </div>
      );
    }

    if (change.type === 'item_added') {
      const item = change.new_item;
      return (
        <div key={idx} style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '3px', color: '#059669' }}>➕ New Item Added</div>
          <div style={{ fontSize: '9px', marginLeft: '10px' }}>
            <div><strong>Type:</strong> {item.booking_type}</div>
            {item.weight && <div><strong>Weight:</strong> {item.weight}g</div>}
          </div>
        </div>
      );
    }

    if (change.type === 'item_deleted') {
      const item = change.old_item;
      return (
        <div key={idx} style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '3px', color: '#dc2626' }}>❌ Item Removed</div>
          <div style={{ fontSize: '9px', marginLeft: '10px' }}>
            <div><strong>Type:</strong> {item.booking_type}</div>
            {item.weight && <div><strong>Weight:</strong> {item.weight}g</div>}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div id="booking-a4-print-content" className="print-content-hidden">
      {booking?.has_provisional_changes && <div className="provisional-watermark">PROVISIONAL</div>}
      <div style={{ 
        fontFamily: 'Arial, sans-serif',
        fontSize: '10px',
        lineHeight: '1.3',
        color: '#000',
        padding: '10px',
        maxWidth: '100%'
      }}>
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '15px', borderBottom: '2px solid #000', paddingBottom: '10px' }}>
          <h1 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '3px', margin: '0' }}>BOOKING DETAILS</h1>
          <div style={{ fontSize: '14px', fontWeight: 'bold' }}>Booking ID: {booking.booking_id}</div>
          <div style={{ fontSize: '11px' }}>Date: {formatDate(booking.booking_date || booking.created_at)} | Status: {booking.status}</div>
        </div>

        {/* Customer & Rate Info */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
          <div style={{ border: '1px solid #000', padding: '8px', borderRadius: '4px' }}>
            <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '6px', borderBottom: '1px solid #ddd', paddingBottom: '3px', margin: '0 0 6px 0' }}>👤 CUSTOMER</h2>
            {customer && (
              <div style={{ fontSize: '9px', lineHeight: '1.4' }}>
                <div><strong>Name:</strong> {customer.name}</div>
                {customer.mobile && <div><strong>Mobile:</strong> {customer.mobile}</div>}
                {customer.alternate_mobile && <div><strong>Alt:</strong> {customer.alternate_mobile}</div>}
                {customer.email && <div><strong>Email:</strong> {customer.email}</div>}
                {customer.address && <div><strong>Address:</strong> {customer.address}</div>}
              </div>
            )}
          </div>

          {booking.base24k_rate_ex_gst && (
            <div style={{ border: '1px solid #f59e0b', padding: '8px', borderRadius: '4px', backgroundColor: '#fffbeb' }}>
              <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '6px', borderBottom: '1px solid #f59e0b', paddingBottom: '3px', margin: '0 0 6px 0' }}>💰 RATE INFO</h2>
              <div style={{ fontSize: '9px', lineHeight: '1.4' }}>
                <div><strong>24K Base Rate:</strong> ₹{formatCurrency(booking.base24k_rate_ex_gst)}/g</div>
                {booking.lock_date && <div><strong>Lock Date:</strong> {formatDate(booking.lock_date)}</div>}
                {booking.gst_applicable === false ? (
                  <div><strong>GST:</strong> Kaccha (No GST)</div>
                ) : (
                  <div><strong>GST:</strong> {booking.gst_percent_snapshot || 3}%</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Booking Items */}
        <div style={{ marginBottom: '15px' }}>
          <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '8px', borderBottom: '1px solid #000', paddingBottom: '3px', margin: '0 0 8px 0' }}>📦 BOOKING ITEMS ({booking.items?.length || 0})</h2>
          
          {booking.items?.map((item, idx) => (
            <div key={idx} style={{ marginBottom: '10px', border: '1px solid #ddd', padding: '8px', borderRadius: '4px' }}>
              <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '5px', color: '#d97706' }}>#{idx + 1} {item.booking_type}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4px', fontSize: '8px' }}>
                <div><strong>Weight:</strong> {item.weight}g</div>
                {item.public_remark && (
                  <div style={{ gridColumn: '1 / -1', marginTop: '4px', padding: '4px', backgroundColor: '#f3f4f6', borderRadius: '2px' }}>
                    <strong>Remark:</strong> {item.public_remark}
                  </div>
                )}
                {item.internal_remark && (
                  <div style={{ gridColumn: '1 / -1', marginTop: '4px', padding: '4px', backgroundColor: '#fef3c7', borderRadius: '2px' }}>
                    <strong>Internal:</strong> {item.internal_remark}
                  </div>
                )}
              </div>
            </div>
          ))}
          
          <div style={{ marginTop: '8px', padding: '6px', border: '1px solid #000', borderRadius: '4px', backgroundColor: '#f9fafb' }}>
            <div style={{ fontSize: '10px', fontWeight: 'bold' }}>Total Weight: {totals.totalWeight.toFixed(2)}g</div>
          </div>
        </div>

        {/* Payments & Deposits */}
        {booking.advance_payments && booking.advance_payments.length > 0 && (
          <div style={{ marginBottom: '15px' }}>
            <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '8px', borderBottom: '1px solid #000', paddingBottom: '3px', margin: '0 0 8px 0' }}>💳 PAYMENTS & DEPOSITS</h2>

            {Object.keys(paymentsByMode).map((mode, modeIdx) => (
              <div key={modeIdx} style={{ marginBottom: '10px', border: '1px solid #ddd', padding: '6px', borderRadius: '4px' }}>
                <h3 style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '4px', color: '#059669', margin: '0 0 4px 0' }}>{mode}</h3>

                {mode === 'Old Gold' ? (
                  <div style={{ fontSize: '8px' }}>
                    {paymentsByMode[mode].map((payment, payIdx) => (
                      <div key={payIdx} style={{ marginBottom: '6px' }}>
                        <div><strong>Date:</strong> {formatDate(payment.date)} | <strong>Amt:</strong> ₹{formatCurrency(payment.amount || 0)}</div>
                        {payment.items && payment.items.length > 0 && (
                          <div style={{ marginTop: '3px', marginLeft: '8px' }}>
                            {payment.items.map((item, itemIdx) => (
                              <div key={itemIdx} style={{ marginBottom: '2px', fontSize: '7px' }}>
                                {item.item_name} - Gross: {item.gross_wt}g, Net: {item.net_wt}g, Fine: {item.fine_wt}g ({item.purity}%)
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : mode === 'Metal Credit' ? (
                  /* Metal Credit - Show detailed breakdown with all deposits */
                  <div style={{ fontSize: '8px' }}>
                    {paymentsByMode[mode]
                      .filter(p => parseFloat(p.fine_wt || p.fine_grams || 0) > 0 || (p.deposits && p.deposits.length > 0))
                      .map((payment, payIdx) => {
                        const hasDeposits = payment.deposits && Array.isArray(payment.deposits) && payment.deposits.length > 0;
                        
                        return (
                          <div key={payIdx} style={{ marginBottom: '8px', border: '2px solid #d97706', padding: '6px', borderRadius: '4px', backgroundColor: '#fffbeb' }}>
                            {hasDeposits ? (
                              <>
                                {payment.deposits.map((deposit, depIdx) => (
                                  <div key={depIdx} style={{ marginBottom: '8px', paddingBottom: '6px', borderBottom: depIdx < payment.deposits.length - 1 ? '1px dashed #d97706' : 'none' }}>
                                    <div style={{ fontWeight: 'bold', fontSize: '10px', marginBottom: '4px', color: '#92400e' }}>
                                      Deposit {depIdx + 1}
                                      {deposit.item_name && `: ${deposit.item_name}`}
                                    </div>
                                    
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8px', marginBottom: '4px' }}>
                                      <tbody>
                                        <tr style={{ borderBottom: '1px solid #fde68a' }}>
                                          <td style={{ padding: '2px', fontWeight: 'bold', width: '30%' }}>Date</td>
                                          <td style={{ padding: '2px' }}>{formatDate(deposit.date || payment.date)}</td>
                                        </tr>
                                        {(deposit.type || deposit.type_name_other) && (
                                          <tr style={{ borderBottom: '1px solid #fde68a' }}>
                                            <td style={{ padding: '2px', fontWeight: 'bold' }}>Type</td>
                                            <td style={{ padding: '2px' }}>{deposit.type_name_other || deposit.type}</td>
                                          </tr>
                                        )}
                                        {deposit.gross_wt && (
                                          <tr style={{ borderBottom: '1px solid #fde68a' }}>
                                            <td style={{ padding: '2px', fontWeight: 'bold' }}>Gross Wt</td>
                                            <td style={{ padding: '2px' }}>{deposit.gross_wt}g</td>
                                          </tr>
                                        )}
                                        {deposit.less_wt && (
                                          <tr style={{ borderBottom: '1px solid #fde68a' }}>
                                            <td style={{ padding: '2px', fontWeight: 'bold' }}>Less Wt</td>
                                            <td style={{ padding: '2px' }}>{deposit.less_wt}g</td>
                                          </tr>
                                        )}
                                        {deposit.net_wt && (
                                          <tr style={{ borderBottom: '1px solid #fde68a' }}>
                                            <td style={{ padding: '2px', fontWeight: 'bold' }}>Net Wt</td>
                                            <td style={{ padding: '2px' }}>{deposit.net_wt}g</td>
                                          </tr>
                                        )}
                                        {(deposit.purity || deposit.purity_label) && (
                                          <tr style={{ borderBottom: '1px solid #fde68a' }}>
                                            <td style={{ padding: '2px', fontWeight: 'bold' }}>Purity</td>
                                            <td style={{ padding: '2px' }}>
                                              {deposit.purity_label || `${deposit.purity}%`}
                                            </td>
                                          </tr>
                                        )}
                                        <tr>
                                          <td style={{ padding: '2px', fontWeight: 'bold', fontSize: '9px' }}>Fine Wt</td>
                                          <td style={{ padding: '2px', fontWeight: 'bold', fontSize: '9px' }}>{deposit.fine_wt || 0}g</td>
                                        </tr>
                                      </tbody>
                                    </table>
                                    
                                    {deposit.reference && (
                                      <div style={{ marginTop: '3px', fontSize: '7px', color: '#92400e' }}>
                                        <strong>Ref:</strong> {deposit.reference}
                                      </div>
                                    )}
                                    
                                    {deposit.photos && deposit.photos.length > 0 && (
                                      <div style={{ marginTop: '4px' }}>
                                        <div style={{ fontSize: '8px', fontWeight: 'bold', marginBottom: '3px', color: '#92400e' }}>
                                          Photos ({deposit.photos.length}):
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px' }}>
                                          {deposit.photos.slice(0, 3).map((photo, photoIdx) => {
                                            const photoUrl = typeof photo === 'string' ? photo : (photo.edited || photo.original);
                                            return photoUrl ? (
                                              <img 
                                                key={photoIdx}
                                                src={photoUrl} 
                                                alt={`Photo ${photoIdx + 1}`}
                                                crossOrigin="anonymous"
                                                style={{ 
                                                  width: '100%', 
                                                  height: '60px', 
                                                  objectFit: 'cover', 
                                                  border: '1px solid #d97706',
                                                  borderRadius: '2px',
                                                  display: 'block'
                                                }}
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                              />
                                            ) : null;
                                          })}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </>
                            ) : (
                              <>
                                <div style={{ marginBottom: '3px' }}>
                                  {formatDate(payment.date)} | Fine: {payment.fine_wt || 0}g
                                  {payment.metal_type && ` | Type: ${payment.metal_type}`}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    {paymentsByMode[mode].length > 0 && (
                      <div style={{ marginTop: '4px', paddingTop: '4px', borderTop: '2px solid #d97706', textAlign: 'right', fontWeight: 'bold', fontSize: '9px', color: '#92400e' }}>
                        Total Fine Gold: {paymentsByMode[mode].reduce((sum, p) => {
                          if (p.deposits && p.deposits.length > 0) {
                            return sum + p.deposits.reduce((depSum, dep) => depSum + parseFloat(dep.fine_wt || 0), 0);
                          }
                          return sum + parseFloat(p.fine_wt || p.fine_grams || 0);
                        }, 0).toFixed(3)}g
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: '8px' }}>
                    {paymentsByMode[mode].map((payment, payIdx) => {
                      const amount = parseFloat(payment.amount) || 0;
                      const isRefund = payment.transaction_type === 'REFUND';
                      return (
                        <div key={payIdx} style={{ marginBottom: '3px', color: isRefund ? '#dc2626' : '#000' }}>
                          {formatDate(payment.date)} | {isRefund ? '-' : ''}₹{formatCurrency(Math.abs(amount))}
                          {isRefund && ' (REFUND)'}
                          {payment.note && ` | ${payment.note}`}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div style={{ marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #ddd', textAlign: 'right', fontWeight: 'bold', fontSize: '9px' }}>
                  Total: ₹{formatCurrency(
                    paymentsByMode[mode].reduce((sum, p) => {
                      const amount = parseFloat(p.amount) || 0;
                      return p.transaction_type === 'REFUND' ? sum - amount : sum + amount;
                    }, 0)
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Booking Summary */}
        <div style={{ marginBottom: '15px', border: '2px solid #000', padding: '8px', borderRadius: '4px', backgroundColor: '#f9fafb' }}>
          <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '6px', margin: '0 0 6px 0' }}>📊 BOOKING SUMMARY</h2>
          <div style={{ fontSize: '10px' }}>
            {totals.subtotal > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                <span>Estimated Value:</span>
                <span style={{ fontWeight: 'bold' }}>₹{formatCurrency(totals.subtotal)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
              <span>Advance Paid:</span>
              <span style={{ fontWeight: 'bold', color: '#059669' }}>₹{formatCurrency(totals.totalAdvance)}</span>
            </div>
            {totals.metalCredit > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                <span>Metal Credit:</span>
                <span style={{ fontWeight: 'bold', color: '#d97706' }}>{totals.metalCredit.toFixed(2)}g</span>
              </div>
            )}
            {totals.subtotal > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '6px', borderTop: '1px solid #000', fontSize: '11px' }}>
                <span style={{ fontWeight: 'bold' }}>Balance Due:</span>
                <span style={{ fontWeight: 'bold', color: totals.balance > 0 ? '#dc2626' : '#059669' }}>₹{formatCurrency(totals.balance)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Signatures */}
        {(booking.management_signature || booking.customer_signature) && (
          <div style={{ marginBottom: '15px' }}>
            <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '6px', borderBottom: '1px solid #000', paddingBottom: '3px', margin: '0 0 6px 0' }}>✍️ SIGNATURES</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {booking.management_signature && (
                <div style={{ border: '1px solid #ddd', padding: '6px', borderRadius: '4px' }}>
                  <div style={{ fontSize: '9px', fontWeight: 'bold', marginBottom: '4px' }}>Management</div>
                  <img src={booking.management_signature} alt="Management" style={{ width: '100%', maxWidth: '120px', height: 'auto', maxHeight: '40px', border: '1px solid #ddd' }} />
                </div>
              )}
              {booking.customer_signature && (
                <div style={{ border: '1px solid #ddd', padding: '6px', borderRadius: '4px' }}>
                  <div style={{ fontSize: '9px', fontWeight: 'bold', marginBottom: '4px' }}>Customer</div>
                  <img src={booking.customer_signature} alt="Customer" style={{ width: '100%', maxWidth: '120px', height: 'auto', maxHeight: '40px', border: '1px solid #ddd' }} />
                  {customer && customer.name && <div style={{ fontSize: '8px', marginTop: '3px' }}>Name: {customer.name}</div>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Revision History */}
        {booking.revision_history && booking.revision_history.length > 0 && (
          <div style={{ marginBottom: '10px' }}>
            <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '6px', borderBottom: '1px solid #000', paddingBottom: '3px', margin: '0 0 6px 0' }}>📝 REVISION HISTORY</h2>
            {booking.revision_history.map((revision, idx) => {
              const changes = revision.changes || [];
              return (
                <div key={idx} style={{ marginBottom: '10px', border: '1px solid #ddd', padding: '6px', borderRadius: '4px', backgroundColor: '#fef3c7' }}>
                  <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '4px' }}>Revision #{revision.revision_number || idx + 1} - {formatDate(revision.timestamp)}</div>
                  <div style={{ fontSize: '8px', marginBottom: '4px' }}>
                    <strong>By:</strong> {revision.edited_by || 'Unknown'}
                    {revision.approved_by && ` | Approved by: ${revision.approved_by}`}
                  </div>
                  {changes.length > 0 && (
                    <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #e5e7eb' }}>
                      {changes.map((change, changeIdx) => renderRevisionChange(change, changeIdx))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: '10px', paddingTop: '6px', borderTop: '1px solid #000', textAlign: 'center', fontSize: '8px', color: '#6b7280' }}>
          <div>Computer-generated document | Printed: {new Date().toLocaleString('en-IN')}</div>
        </div>

      </div>
    </div>
  );
};

export default BookingPrintA4;